/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';

import { Td } from './css';
import FieldGuide from '../../../../form/fields/common/fieldGuide';
import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';
import getFormattedPricing from '../../utils';

const Row = ({
  rowData,
  viewType,
  isRenewal,
  ratePlans,
  hardware,
  setHardware
}) => {
  const {
    fields: {
      hardwareName,
      hardwareId,
      hardwareHasIncludedUnits,
      unitPrice,
      toolTip,
      hideForSimpleView,
      isAccessory
    }
  } = rowData;

  if (viewType === 'simpleView' && hideForSimpleView) return null;

  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: { value3year, value2year, valueMTM }
  } = localizedStrings;

  const [includedQuantity, setIncludedQuantity] = useState(0);
  const [additionalQuantity, setAdditionalQuantity] = useState(() =>
    _.get(hardware, `${hardwareName}.additionalQuantity`, 0)
  );
  const isComponentMounted = useRef(false);
  const rowRef = useRef(false);

  useEffect(() => {
    if (!isComponentMounted.current) {
      isComponentMounted.current = true;
      return;
    }

    const updatedRatePlans = {
      ...hardware,
      [hardwareName]: {
        includedQuantity,
        additionalQuantity,
        unitPrice,
        isAccessory
      }
    };
    setHardware(updatedRatePlans);
  }, [additionalQuantity, unitPrice]);

  useEffect(() => {
    if (viewType && rowRef.current) {
      setAdditionalQuantity(0);
      setIncludedQuantity(0);
    }
    rowRef.current = true;
  }, [viewType]);

  /**
   * When user adds a rate plan
   * update the included units in hardware automatically
   */
  useEffect(() => {
    if (hardwareHasIncludedUnits) {
      const includedUnits = Object.keys(ratePlans).reduce((acc, current) => {
        const totalunits = Object.keys(ratePlans[current]).reduce(
          (rateplanAcc, item) => {
            const { quantity, term } = ratePlans[current][item];
            if (item === hardwareId && [value2year, value3year].includes(term))
              return rateplanAcc + +quantity;

            return rateplanAcc;
          },
          0
        );
        return acc + totalunits;
      }, 0);
      const mtmUnits = Object.keys(ratePlans).reduce((acc, current) => {
        const totalunits = Object.keys(ratePlans[current]).reduce(
          (rateplanAcc, item) => {
            const { quantity, term } = ratePlans[current][item];
            if (item === hardwareId && term === valueMTM)
              return rateplanAcc + +quantity;

            return rateplanAcc;
          },
          0
        );
        return acc + totalunits;
      }, 0);
      if (includedUnits > 0) {
        const updatedRatePlans = {
          ...hardware,
          [hardwareName]: {
            includedQuantity: includedUnits,
            additionalQuantity: mtmUnits,
            unitPrice,
            isAccessory
          }
        };
        setHardware(updatedRatePlans);
      }
      setAdditionalQuantity(mtmUnits);
      setIncludedQuantity(includedUnits);
    }
  }, [ratePlans]);

  return (
    <tr>
      <Td style={{ background: '#f7f7f7', fontWeight: '700' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <p>{hardwareName}</p>
          {toolTip && (
            <div style={{ margin: '-0.35rem 0.75rem' }}>
              <FieldGuide isRichText tooltip={toolTip} />
            </div>
          )}
        </div>
      </Td>
      {!isRenewal && (
        <Td>
          <input
            disabled
            style={{ background: '#eee' }}
            className="frg-input-field color-light"
            value={includedQuantity}
          />
        </Td>
      )}
      <Td>
        <input
          className="frg-input-field color-light"
          pattern="[0-9]"
          value={additionalQuantity}
          onChange={e => {
            setAdditionalQuantity(+e.target.value);
          }}
        />
      </Td>
      {!isRenewal && (
        <Td>
          <input
            disabled
            style={{ background: '#eee' }}
            className="frg-input-field color-light"
            value={includedQuantity + additionalQuantity}
          />
        </Td>
      )}
      <Td>{getFormattedPricing(unitPrice)}</Td>
      <Td>{getFormattedPricing(+unitPrice * additionalQuantity)}</Td>
    </tr>
  );
};

Row.propTypes = {
  rowData: PropTypes.shape({
    fields: PropTypes.shape({
      hardwareName: PropTypes.string,
      hardwareId: PropTypes.string,
      hardwareHasIncludedUnits: PropTypes.bool,
      unitPrice: PropTypes.number,
      toolTip: PropTypes.shape({
        data: PropTypes.object,
        content: PropTypes.array,
        nodeType: PropTypes.string
      }),
      hideForSimpleView: PropTypes.bool.isRequired,
      isAccessory: PropTypes.bool.isRequired
    })
  }).isRequired,
  viewType: PropTypes.string.isRequired,
  isRenewal: PropTypes.bool.isRequired,
  ratePlans: PropTypes.arrayOf(PropTypes.object).isRequired,
  hardware: PropTypes.arrayOf(PropTypes.object).isRequired,
  setHardware: PropTypes.func.isRequired
};

export default Row;
